import React, { useState } from 'react';
import { View, Text, TouchableOpacity, Modal, FlatList, Platform } from 'react-native';

import { useLanguageStore } from '../../features/quiz-core/languageStore';
import { SUPPORTED_LANGUAGES } from '../LanguagePicker';

interface LandingLanguageSelectorProps {
  style?: any;
}

const LandingLanguageSelector: React.FC<LandingLanguageSelectorProps> = ({ style }) => {
  const { language, setLanguage } = useLanguageStore();
  const [isOpen, setIsOpen] = useState(false);

  const currentLanguage = SUPPORTED_LANGUAGES.find(lang => lang.code === language);

  const handleLanguageSelect = (langCode: string) => {
    setLanguage(langCode);
    setIsOpen(false);
    
    // Save to localStorage for pre-auth experience
    if (Platform.OS === 'web' && typeof localStorage !== 'undefined') {
      localStorage.setItem('user_selected_language', langCode);
    }
  };

  const LanguageItem = ({ item }: { item: typeof SUPPORTED_LANGUAGES[0] }) => (
    <TouchableOpacity
      style={{
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(246, 190, 58, 0.14)',
      }}
      onPress={() => handleLanguageSelect(item.code)}
    >
      <Text style={{ fontSize: 18, marginRight: 12 }}>{getFlagEmoji(item.code)}</Text>
      <Text style={{
        color: language === item.code ? '#FFE8A3' : '#FFF8E8',
        fontSize: 16,
        fontWeight: language === item.code ? '600' : '400',
      }}>
        {item.nativeName}
      </Text>
      {language === item.code && (
        <Text style={{ color: '#F6BE3A', marginLeft: 'auto', fontSize: 16 }}>✓</Text>
      )}
    </TouchableOpacity>
  );

  return (
    <View style={[{
      position: 'absolute',
      top: Platform.OS === 'web' ? 20 : 50,
      right: 20,
      zIndex: 1000,
    }, style]}>
      {/* Trigger Button */}
      <TouchableOpacity
        onPress={() => setIsOpen(true)}
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          paddingHorizontal: 12,
          paddingVertical: 8,
          borderRadius: 20,
          backgroundColor: 'rgba(10, 8, 24, 0.88)',
          borderWidth: 1.5,
          borderColor: 'rgba(246, 190, 58, 0.42)',
          shadowColor: '#F6BE3A',
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.18,
          shadowRadius: 8,
          elevation: 3,
        }}
        accessibilityLabel={`Current language: ${currentLanguage?.nativeName}. Tap to change language.`}
        accessibilityRole="button"
      >
        <Text style={{ fontSize: 16, marginRight: 6 }}>🌐</Text>
        <Text style={{ fontSize: 14, marginRight: 6 }}>{getFlagEmoji(language)}</Text>
        <Text style={{
          color: '#FFF8E8',
          fontSize: 14,
          fontWeight: '600',
        }}>
          {currentLanguage?.nativeName}
        </Text>
        <Text style={{ color: '#F6BE3A', marginLeft: 4, fontSize: 12 }}>▼</Text>
      </TouchableOpacity>

      {/* Language Selection Modal */}
      <Modal
        visible={isOpen}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setIsOpen(false)}
      >
        <TouchableOpacity
          style={{
            flex: 1,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            justifyContent: 'center',
            alignItems: 'center',
          }}
          activeOpacity={1}
          onPress={() => setIsOpen(false)}
        >
          <View
            style={{
              backgroundColor: '#09051A',
              borderRadius: 16,
              borderWidth: 1,
              borderColor: 'rgba(246, 190, 58, 0.36)',
              maxHeight: '70%',
              width: '80%',
              maxWidth: 320,
              shadowColor: '#F6BE3A',
              shadowOffset: { width: 0, height: 8 },
              shadowOpacity: 0.12,
              shadowRadius: 16,
              elevation: 10,
            }}
            onStartShouldSetResponder={() => true}
          >
            {/* Header */}
            <View style={{
              paddingHorizontal: 16,
              paddingVertical: 16,
              borderBottomWidth: 1,
              borderBottomColor: 'rgba(246, 190, 58, 0.14)',
            }}>
              <Text style={{
                color: '#FFF8E8',
                fontSize: 18,
                fontWeight: '600',
                textAlign: 'center',
              }}>
                {getChooseLanguageText(language)}
              </Text>
            </View>

            {/* Language List */}
            <FlatList
              data={SUPPORTED_LANGUAGES}
              keyExtractor={(item) => item.code}
              renderItem={LanguageItem}
              style={{ maxHeight: 400 }}
              showsVerticalScrollIndicator={false}
            />
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

// Helper function to get flag emoji for language codes
const getFlagEmoji = (langCode: string): string => {
  const flagMap: Record<string, string> = {
    en: '🇺🇸',
    es: '🇪🇸', 
    fr: '🇫🇷',
    de: '🇩🇪',
    pt: '🇧🇷',
    ru: '🇷🇺',
    tl: '🇵🇭',
    it: '🇮🇹',
    am: '🇪🇹',
    ko: '🇰🇷',
  };
  return flagMap[langCode] || '🌐';
};

// Helper function to get "Choose Language" text in current language
const getChooseLanguageText = (langCode: string): string => {
  const textMap: Record<string, string> = {
    en: 'Choose Language',
    es: 'Elegir Idioma',
    fr: 'Choisir la Langue',
    de: 'Sprache Wählen',
    pt: 'Escolher Idioma',
    ru: 'Выбрать Язык',
    tl: 'Piliin ang Wika',
    it: 'Scegli Lingua',
    am: 'ቋንቋ ምረጥ',
    ko: '언어 선택',
  };
  return textMap[langCode] || 'Choose Language';
};

export default LandingLanguageSelector;
